import { useState } from "react";
import { Check, FolderPlus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const Chip = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${active ? "bg-navy text-white border-navy" : "bg-white border-border hover:border-navy"}`}>
    {children}
  </button>
);

const NameForm = ({ initial = "", onSubmit, onCancel, testId }) => {
  const [name, setName] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSubmit(name.trim()); }} className="flex items-center gap-1" data-testid={testId}>
      <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Christmas"
        data-testid={`${testId}-input`} className="h-8 w-40 bg-white text-sm" />
      <button type="submit" data-testid={`${testId}-save`} className="p-1.5 text-navy hover:text-pinkb" aria-label="Save"><Check className="h-4 w-4" /></button>
      <button type="button" onClick={onCancel} className="p-1.5 text-muted-foreground" aria-label="Cancel"><X className="h-4 w-4" /></button>
    </form>
  );
};

export const FolderBar = ({ folders, setFolders, items, active, setActive, onDeleted }) => {
  const [mode, setMode] = useState(null);
  const count = (id) => items.filter((i) => (i.folder_ids || []).includes(id)).length;
  const current = folders.find((f) => f.id === active);

  const create = async (name) => {
    try {
      const { data } = await api.post("/playbook/folders", { name });
      setFolders((l) => [...l, data]); setActive(data.id); setMode(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (name) => {
    try {
      const { data } = await api.patch(`/playbook/folders/${active}`, { name });
      setFolders((l) => l.map((f) => (f.id === data.id ? data : f))); setMode(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the folder "${current.name}"? The answers stay in your playbook.`)) return;
    try {
      await api.delete(`/playbook/folders/${active}`);
      setFolders((l) => l.filter((f) => f.id !== active)); setActive(null); onDeleted?.();
      toast.success("Folder deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mb-8 space-y-3" data-testid="playbook-folder-bar">
      <div className="flex flex-wrap items-center gap-2">
        <Chip active={!active} onClick={() => setActive(null)} testId="folder-chip-all">All ({items.length})</Chip>
        {folders.map((f) => <Chip key={f.id} active={active === f.id} onClick={() => setActive(f.id)} testId={`folder-chip-${f.id}`}>{f.name} ({count(f.id)})</Chip>)}
        {mode === "new"
          ? <NameForm onSubmit={create} onCancel={() => setMode(null)} testId="folder-new-form" />
          : <button onClick={() => setMode("new")} data-testid="folder-new-btn" className="flex items-center gap-1 text-sm text-pinkb hover:underline px-2"><FolderPlus className="h-4 w-4" />New folder</button>}
      </div>
      {current && (mode === "rename"
        ? <NameForm initial={current.name} onSubmit={rename} onCancel={() => setMode(null)} testId="folder-rename-form" />
        : <div className="flex gap-3 text-xs text-muted-foreground">
            <button onClick={() => setMode("rename")} data-testid="folder-rename-btn" className="flex items-center gap-1 hover:text-navy"><Pencil className="h-3.5 w-3.5" />Rename</button>
            <button onClick={remove} data-testid="folder-delete-btn" className="flex items-center gap-1 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" />Delete folder</button>
          </div>)}
    </div>
  );
};

export const FolderPicker = ({ item, folders, onChange }) => {
  const toggle = async (fid) => {
    const ids = item.folder_ids || [];
    const next = ids.includes(fid) ? ids.filter((x) => x !== fid) : [...ids, fid];
    try {
      const { data } = await api.put(`/playbook/${item.id}/folders`, { folder_ids: next });
      onChange(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!folders.length) return <p className="text-xs text-muted-foreground p-2">Create a folder first using "New folder" above.</p>;
  return (
    <div className="space-y-1" data-testid={`folder-picker-${item.id}`}>
      {folders.map((f) => {
        const on = (item.folder_ids || []).includes(f.id);
        return (
          <button key={f.id} onClick={() => toggle(f.id)} data-testid={`folder-toggle-${item.id}-${f.id}`}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary text-left">
            <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? "bg-navy border-navy text-white" : "border-border"}`}>{on && <Check className="h-3 w-3" />}</span>
            {f.name}
          </button>
        );
      })}
    </div>
  );
};
